import { useState, useEffect } from 'react';
import axios from 'axios';
import PublicHeader from '../components/PublicHeader';
import { PaymentMethodPicker } from '../components/PaymentMethodPicker';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Euro, 
  Clock,
  BookOpen,
  Search,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicCatalog() {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showBankInfo, setShowBankInfo] = useState(false);
  const [bankTransferInfo, setBankTransferInfo] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: ''
  });

  useEffect(() => {
    fetchTrainings();
  }, []);

  useEffect(() => {
    filterTrainings();
  }, [trainings, searchTerm, selectedCategory]);

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      // Only show published trainings
      const published = response.data.filter(t => t.status === 'published');
      setTrainings(published);
    } catch (error) {
      console.error('Error fetching trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTrainings = () => {
    let filtered = trainings;

    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    setFilteredTrainings(filtered);
  };

  const categories = [...new Set(trainings.map(t => t.category))];

  const handleRegister = (training) => {
    setSelectedTraining(training);
    setShowRegistrationForm(true);
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/public/register`, {
        ...formData,
        training_id: selectedTraining.id,
        payment_method: paymentMethod,
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      // Show bank info modal
      setBankTransferInfo(response.data.bank_info);
      setEmailStatus(response.data.email_status || 'skipped');
      setShowRegistrationForm(false);
      setShowBankInfo(true);
    } catch (error) {
      console.error('Error registering:', error);
      alert('Erreur lors de l\'inscription. Veuillez réessayer.\n\n' + (error.response?.data?.detail || error.message));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      <PublicHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filters */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher une formation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="search-catalog"
              />
            </div>

            {categories.length > 0 && (
              <div className="flex items-center gap-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                  data-testid="filter-category"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Trainings Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement des formations...</p>
          </div>
        ) : filteredTrainings.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">Aucune formation disponible pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTrainings.map((training) => (
              <div
                key={training.id}
                className="bg-white rounded-2xl shadow-lg border border-violet-100 overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`public-training-${training.id}`}
              >
                {/* Header with gradient */}
                <div className="h-32 bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-center">
                  <BookOpen className="text-white" size={48} />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">
                      {training.category}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-800 mb-3" data-testid="training-title">
                    {training.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                    {training.description}
                  </p>

                  {/* Info Grid */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Calendar size={18} className="text-violet-500" />
                      <span>
                        {training.start_date 
                          ? new Date(training.start_date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })
                          : 'Date à définir'}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Clock size={18} className="text-violet-500" />
                      <span>{training.duration_hours} heures</span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <MapPin size={18} className="text-violet-500" />
                      <span className="truncate">{training.location}</span>
                    </div>

                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Users size={18} className="text-violet-500" />
                      <span>Places limitées ({training.max_participants} max)</span>
                    </div>
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Euro className="text-violet-600" size={24} />
                      <span className="text-3xl font-bold text-violet-600">
                        {training.price.toFixed(0)}
                      </span>
                      <span className="text-gray-500">€</span>
                    </div>

                    <button
                      onClick={() => handleRegister(training)}
                      className="btn-violet"
                      data-testid={`register-button-${training.id}`}
                    >
                      S'inscrire
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Registration Modal */}
        {showRegistrationForm && selectedTraining && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="registration-modal">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                Inscription à la formation
              </h3>
              <p className="text-violet-600 mb-6">{selectedTraining.title}</p>

              <form onSubmit={handleSubmitRegistration} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="registration-firstname"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="registration-lastname"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="registration-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="registration-phone"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Entreprise (optionnel)
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="input-violet"
                      data-testid="registration-company"
                    />
                  </div>
                </div>

                <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Montant à payer :</span>
                    <span className="text-2xl font-bold text-violet-600">
                      {selectedTraining.price.toFixed(2)} €
                    </span>
                  </div>
                  <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-violet flex-1 disabled:opacity-50"
                    data-testid="submit-registration"
                  >
                    {submitting ? 'Envoi...' : paymentMethod === 'card' ? 'Payer par carte' : 'Valider mon inscription'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRegistrationForm(false);
                      setSelectedTraining(null);
                    }}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                    data-testid="cancel-registration"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bank Transfer Info Modal */}
        {showBankInfo && bankTransferInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="bank-info-modal">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="text-center mb-6">
                <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-4xl">✅</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-2">
                  Pré-inscription réussie !
                </h3>
                <p className="text-gray-600">
                  Merci pour votre inscription. Pour la finaliser, effectuez le virement bancaire avec les informations ci-dessous.
                </p>
              </div>

              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 border-2 border-violet-200 mb-6">
                <h4 className="text-lg font-bold text-violet-700 mb-4 flex items-center gap-2">
                  💳 Coordonnées bancaires
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Titulaire du compte</p>
                    <p className="font-bold text-gray-800" data-testid="bank-holder">{bankTransferInfo.holder}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">IBAN</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold text-gray-800 flex-1 bg-white p-2 rounded border" data-testid="bank-iban">
                        {bankTransferInfo.iban}
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(bankTransferInfo.iban.replace(/\s/g, ''));
                          alert('IBAN copié !');
                        }}
                        className="px-3 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 text-sm"
                      >
                        📋 Copier
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">BIC</p>
                    <p className="font-mono font-bold text-gray-800 bg-white p-2 rounded border" data-testid="bank-bic">{bankTransferInfo.bic}</p>
                  </div>

                  <div className="pt-4 border-t border-violet-200">
                    <p className="text-xs text-gray-500 mb-1">Montant à virer</p>
                    <p className="text-3xl font-bold text-violet-600" data-testid="bank-amount">
                      {bankTransferInfo.amount.toFixed(2)} €
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">⚠️ Référence à mettre dans votre virement</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono font-bold text-red-600 flex-1 bg-red-50 p-2 rounded border border-red-200" data-testid="bank-reference">
                        {bankTransferInfo.reference}
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(bankTransferInfo.reference);
                          alert('Référence copiée !');
                        }}
                        className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                      >
                        📋 Copier
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                {emailStatus === 'success' ? (
                  <p className="text-sm text-amber-800" data-testid="email-status-success">
                    📧 <strong>Un email récapitulatif</strong> avec toutes ces informations vous a été envoyé.
                  </p>
                ) : (
                  <p className="text-sm text-amber-800" data-testid="email-status-failure">
                    ⚠️ <strong>L'envoi automatique par email n'a pas pu aboutir.</strong>{' '}
                    Merci de <strong>noter ou copier les coordonnées bancaires ci-dessus</strong> pour effectuer votre virement.
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  ℹ️ <strong>Prochaines étapes :</strong>
                </p>
                <ol className="text-sm text-blue-800 mt-2 ml-4 list-decimal space-y-1">
                  <li>Effectuez le virement depuis votre banque (n'oubliez pas la référence)</li>
                  <li>Nous confirmons votre inscription sous 2-3 jours ouvrés à réception du virement</li>
                  <li>Vous recevrez votre convocation officielle par email</li>
                </ol>
              </div>

              <button
                onClick={() => {
                  setShowBankInfo(false);
                  setBankTransferInfo(null);
                  setSelectedTraining(null);
                  setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    company: ''
                  });
                }}
                className="btn-violet w-full"
                data-testid="close-bank-info"
              >
                J'ai bien noté, merci !
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-violet-100 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h3 className="text-xl font-bold gradient-text mb-2">Insitumed</h3>
            <p className="text-gray-600 text-sm mb-4">
              Centre de Formation Professionnelle
            </p>
            <p className="text-gray-500 text-xs">
              © 2025 Insitumed - Tous droits réservés
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
